// Reglas de contraseña compartidas entre los flows que dejan al user
// elegir su propia pass:
//   - Registro de empresa (/register)
//   - Cambio de contraseña (/settings → Seguridad)
//
// Es importante que ambos flows usen exactamente la misma política para
// que un user que se acaba de registrar con una pass que pasó las reglas
// pueda cambiarla por otra que también pase las mismas reglas — sin
// sorprenderse con criterios más estrictos en un lado que en el otro.
//
// Política vigente: 8+ caracteres, una mayúscula, una minúscula, un
// número o símbolo. NO usamos la policy más estricta de
// `password-policy.ts` (10 chars + número + símbolo + top-100) porque
// agregaba fricción innecesaria al onboarding y el user pidió esta
// versión más liviana (igual al register).

export const PWD_RULES = {
  min: (p: string) => p.length >= 8,
  upper: (p: string) => /[A-Z]/.test(p),
  lower: (p: string) => /[a-z]/.test(p),
  numOrSym: (p: string) => /[0-9]|[^A-Za-z0-9]/.test(p),
};

export const PWD_RULE_LABELS = {
  min: "Mínimo 8 caracteres",
  upper: "Una mayúscula",
  lower: "Una minúscula",
  numOrSym: "Un número o símbolo",
} as const;

export const PWD_MAX_LENGTH = 200;

/** ¿Cumple todas las reglas? */
export function passwordStrong(p: string): boolean {
  return PWD_RULES.min(p) && PWD_RULES.upper(p) && PWD_RULES.lower(p) && PWD_RULES.numOrSym(p);
}

/**
 * Mensaje único de error para devolver desde el server. Devuelve null
 * si la contraseña cumple todas las reglas.
 */
export function passwordValidationError(p: string): string | null {
  if (!p || typeof p !== "string") return "La contraseña es obligatoria.";
  if (!PWD_RULES.min(p)) return "La contraseña debe tener al menos 8 caracteres.";
  if (p.length > PWD_MAX_LENGTH) return "La contraseña es demasiado larga.";
  if (!PWD_RULES.upper(p)) return "La contraseña debe incluir al menos una mayúscula.";
  if (!PWD_RULES.lower(p)) return "La contraseña debe incluir al menos una minúscula.";
  if (!PWD_RULES.numOrSym(p)) return "La contraseña debe incluir al menos un número o símbolo.";
  return null;
}

export type PasswordStrength = "weak" | "medium" | "strong";

/**
 * Indicador visual de fortaleza. Cuenta cuántas reglas se cumplen y
 * ajusta por longitud. Sirve para que el user vea progreso a medida
 * que tipea, no es un gate (eso lo hace passwordValidationError).
 */
export function passwordStrength(p: string): PasswordStrength {
  if (!p) return "weak";
  let score = 0;
  if (PWD_RULES.min(p)) score++;
  if (PWD_RULES.upper(p)) score++;
  if (PWD_RULES.lower(p)) score++;
  if (PWD_RULES.numOrSym(p)) score++;
  if (p.length >= 12) score++;
  if (p.length >= 16) score++;
  if (score <= 2) return "weak";
  if (score <= 4) return "medium";
  return "strong";
}
